import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Play, Pause, Square, X, Clock, ChevronDown, GripVertical } from "lucide-react";
import { useAuth, API_BASE } from "./AuthContext";
import { toast } from "sonner";
import { logError } from "../lib/log";

const LS_KEY = "pos.floating_timer.v1";
const POS_KEY = "pos.floating_timer.pos.v2";  // v2: bumped to reset users from the v1 low default

// One-time migration — wipe the v1 key so existing users get the new higher default.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem("pos.floating_timer.pos.v1");
  }
} catch { /* SSR / disabled storage */ }

function loadState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : { hidden: false, active: null };
  } catch {
    return { hidden: false, active: null };
  }
}

function saveState(s) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* quota */ }
}

function loadPos() {
  try {
    const raw = localStorage.getItem(POS_KEY);
    return raw ? JSON.parse(raw) : { x: null, y: null };
  } catch {
    return { x: null, y: null };
  }
}

function savePos(p) {
  try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* quota */ }
}

function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export default function FloatingTimerWidget() {
  const { state } = useAuth();
  const hasOrg = !!state?.user?.org_id;
  const [persisted, setPersisted] = useState(() => loadState());
  const [jobs, setJobs] = useState([]);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pos, setPos] = useState(() => loadPos());
  const dragRef = useRef({ dragging: false, offX: 0, offY: 0 });
  const tickRef = useRef(null);

  // Initialise default position bottom-right after mount if not set.
  // Default y is raised well above the bottom (200px buffer) so the widget
  // doesn't overlap dashboard content like the AI Insights card.
  useEffect(() => {
    if (pos.x == null || pos.y == null) {
      const x = Math.max(8, window.innerWidth - 320 - 24);
      const y = Math.max(8, window.innerHeight - 280);
      setPos({ x, y });
      savePos({ x, y });
    }
  }, [pos.x, pos.y]);

  // Load jobs lazily — only when widget is visible AND there's no active timer's job already in list
  useEffect(() => {
    if (!hasOrg || persisted.hidden) return;
    let cancel = false;
    axios.get(`${API_BASE}/jobs`)
      .then((r) => { if (!cancel) setJobs(r.data || []); })
      .catch((e) => logError("floating-timer:jobs-fetch", e));
    return () => { cancel = true; };
  }, [hasOrg, persisted.hidden]);

  // Tick the elapsed timer
  useEffect(() => {
    if (!persisted.active) {
      setElapsedMs(0);
      return;
    }
    const tick = () => {
      const startedMs = new Date(persisted.active.started_at).getTime();
      const accumulatedPaused = persisted.active.paused_accum_ms || 0;
      if (persisted.active.paused_at) {
        const pausedAtMs = new Date(persisted.active.paused_at).getTime();
        setElapsedMs(pausedAtMs - startedMs - accumulatedPaused);
      } else {
        setElapsedMs(Date.now() - startedMs - accumulatedPaused);
      }
    };
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [persisted.active]);

  const persistAndSetState = useCallback((s) => {
    setPersisted(s);
    saveState(s);
  }, []);

  const onStart = useCallback((jobId) => {
    if (!jobId) {
      toast.error("Pick a job first");
      return;
    }
    const job = jobs.find((j) => j.job_id === jobId);
    persistAndSetState({
      ...persisted,
      hidden: false,
      active: {
        job_id: jobId,
        job_title: job?.title || "Untitled job",
        started_at: new Date().toISOString(),
        paused_at: null,
        paused_accum_ms: 0,
      },
    });
    setJobsOpen(false);
  }, [jobs, persisted, persistAndSetState]);

  const onPause = useCallback(() => {
    if (!persisted.active) return;
    if (persisted.active.paused_at) {
      // resume
      const pausedAt = new Date(persisted.active.paused_at).getTime();
      const accum = (persisted.active.paused_accum_ms || 0) + (Date.now() - pausedAt);
      persistAndSetState({
        ...persisted,
        active: { ...persisted.active, paused_at: null, paused_accum_ms: accum },
      });
    } else {
      persistAndSetState({
        ...persisted,
        active: { ...persisted.active, paused_at: new Date().toISOString() },
      });
    }
  }, [persisted, persistAndSetState]);

  const onStop = useCallback(async () => {
    if (!persisted.active) return;
    const minutes = Math.max(1, Math.round(elapsedMs / 60000));
    if (minutes < 1) {
      toast.error("Need at least 1 minute logged");
      return;
    }
    try {
      await axios.post(`${API_BASE}/time-entries`, {
        job_id: persisted.active.job_id,
        minutes,
        billable: true,
        note: `Floating timer: ${persisted.active.job_title}`,
      });
      toast.success(`${minutes}m logged to "${persisted.active.job_title}"`);
      persistAndSetState({ ...persisted, active: null });
      setSelectedJobId(null);
    } catch (e) {
      logError("floating-timer:log-time", e);
      toast.error("Failed to log time — keep the timer running and try again");
    }
  }, [persisted, persistAndSetState, elapsedMs]);

  const onHide = useCallback(() => {
    persistAndSetState({ ...persisted, hidden: true });
  }, [persisted, persistAndSetState]);

  const onShow = useCallback(() => {
    persistAndSetState({ ...persisted, hidden: false });
  }, [persisted, persistAndSetState]);

  // Drag handlers
  const onDragStart = useCallback((e) => {
    e.preventDefault();
    const rect = e.currentTarget.parentNode.getBoundingClientRect();
    dragRef.current = {
      dragging: true,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
    };
    const move = (ev) => {
      if (!dragRef.current.dragging) return;
      const newX = Math.max(8, Math.min(window.innerWidth - 320 - 8, ev.clientX - dragRef.current.offX));
      const newY = Math.max(8, Math.min(window.innerHeight - 80 - 8, ev.clientY - dragRef.current.offY));
      setPos({ x: newX, y: newY });
    };
    const up = () => {
      dragRef.current.dragging = false;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      // persist
      setPos((p) => { savePos(p); return p; });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, []);

  if (!hasOrg) return null;

  // Hidden state — show small pill to reopen
  if (persisted.hidden) {
    return (
      <button
        type="button"
        onClick={onShow}
        data-testid="floating-timer-show"
        className="fixed bottom-4 right-4 z-50 flex items-center gap-1.5 px-3 py-2 rounded-full shadow-md text-[12px] font-medium"
        style={{ background: "var(--ink)", color: "var(--paper)" }}
        title="Show timer"
      >
        <Clock size={14} /> Timer
      </button>
    );
  }

  const running = !!persisted.active && !persisted.active.paused_at;
  const paused = !!persisted.active && !!persisted.active.paused_at;

  return (
    <div
      data-testid="floating-timer"
      className="fixed z-50 rounded-lg shadow-lg border"
      style={{
        left: pos.x ?? undefined,
        top: pos.y ?? undefined,
        width: 320,
        background: "var(--paper)",
        borderColor: "var(--border)",
        userSelect: "none",
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b cursor-grab active:cursor-grabbing"
           style={{ borderColor: "var(--border)" }}
           onMouseDown={onDragStart}
           data-testid="floating-timer-drag-bar"
           title="Drag to move anywhere on screen">
        <span
          data-testid="floating-timer-drag"
          className="text-[var(--ink-3)] pointer-events-none"
        >
          <GripVertical size={14} />
        </span>
        <div className="text-[12px] font-medium text-[var(--ink-2)] flex-1 pointer-events-none">Timer</div>
        <button
          type="button"
          onClick={onHide}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="floating-timer-hide"
          className="text-[var(--ink-3)] hover:text-[var(--accent)] p-1"
          title="Hide"
        >
          <X size={14} />
        </button>
      </div>

      <div className="px-3 py-3">
        {persisted.active ? (
          <>
            <div className="text-[11px] uppercase tracking-wide text-[var(--ink-3)]">Working on</div>
            <div
              className="text-[14px] font-medium text-[var(--ink)] truncate mb-2"
              data-testid="floating-timer-job-title"
              title={persisted.active.job_title}
            >
              {persisted.active.job_title}
            </div>
            <div
              data-testid="floating-timer-elapsed"
              className="text-[28px] font-mono font-medium tracking-tight"
              style={{ color: paused ? "var(--ink-3)" : "var(--ink)" }}
            >
              {formatElapsed(elapsedMs)}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={onPause}
                data-testid="floating-timer-pause"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium border hover:bg-[var(--subtle)] transition"
                style={{ borderColor: "var(--border)", color: "var(--ink-2)" }}
              >
                {paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
              </button>
              <button
                type="button"
                onClick={onStop}
                data-testid="floating-timer-stop"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium transition"
                style={{ background: "var(--ink)", color: "var(--paper)" }}
              >
                <Square size={13} /> Stop &amp; log
              </button>
            </div>
            {running && (
              <div className="text-[10px] text-[var(--ink-3)] mt-2">Running… stop to log to timesheet</div>
            )}
            {paused && (
              <div className="text-[10px] text-[var(--accent)] mt-2">Paused</div>
            )}
          </>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-wide text-[var(--ink-3)] mb-1">Pick a job</div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setJobsOpen((v) => !v)}
                data-testid="floating-timer-job-picker"
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-[13px] text-left hover:bg-[var(--subtle)] transition"
                style={{ borderColor: "var(--border)" }}
              >
                <span className="truncate">
                  {selectedJobId
                    ? jobs.find((j) => j.job_id === selectedJobId)?.title || "Select a job"
                    : "Select a job"}
                </span>
                <ChevronDown size={14} />
              </button>
              {jobsOpen && (
                <div
                  className="absolute left-0 right-0 bottom-full mb-1 max-h-64 overflow-y-auto rounded-md border shadow-md z-10"
                  style={{ background: "var(--paper)", borderColor: "var(--border)" }}
                  data-testid="floating-timer-job-list"
                >
                  {jobs.length === 0 && (
                    <div className="px-3 py-2 text-[12px] text-[var(--ink-3)]">No jobs yet</div>
                  )}
                  {jobs.filter((j) => j.status !== "done").map((j) => (
                    <button
                      key={j.job_id}
                      type="button"
                      onClick={() => { setSelectedJobId(j.job_id); setJobsOpen(false); }}
                      className="w-full text-left px-3 py-2 text-[13px] hover:bg-[var(--subtle)] truncate"
                    >
                      {j.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onStart(selectedJobId)}
              data-testid="floating-timer-start"
              disabled={!selectedJobId}
              className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium transition disabled:opacity-50"
              style={{ background: "var(--ink)", color: "var(--paper)" }}
            >
              <Play size={13} /> Start timer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
